import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ppaUrls } from 'config/urls';
import React, { createContext, FC, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { getAuthUserMail } from 'utils/helperMethods';
import {
  getSimulationPeriodPresetIdsFromMeta,
  getPpaScenarioCountFromResponse,
  getPpaScenariosFromResponse,
  mergePeriodTimestampsIntoScenarioManagerMeta,
  parsePeriodTimestampsResponse,
  type PpaPeriodTimestamp,
} from 'utils/ppaHelpers';

type ScenarioManagerColumn = {
  id_type?: string;
  id?: string;
  label?: string;
  is_default?: boolean;
  is_sort?: boolean;
  is_filter?: boolean;
  options?: {
    id_type?: string;
    id?: string;
    items?: string[];
  };
};

type ScenarioManagerInput = {
  input_id?: string;
  input_label?: string;
  input_type?: string;
  info?: string;
  placeholder?: string;
  attribute_type?: string;
  dimension?: string;
  is_ppa_scope_key?: boolean;
  drop_down?: Array<{
    id?: string;
    label?: string;
    start_timestamp?: number;
    end_timestamp?: number;
  }>;
};

type ScenarioManagerFeature = {
  feature_id?: string;
  feature_name?: string;
  feature_type?: string;
  options?: {
    columns?: ScenarioManagerColumn[];
    inputs?: ScenarioManagerInput[];
  };
};

type ScenarioManagerMetaResponse = {
  title?: string;
  subtitle?: string;
  page_limit?: number;
  child_limit?: number;
  analytics_enabled?: boolean;
  features?: ScenarioManagerFeature[];
};

type ScenarioManagerSimulationPeriod = {
  start_timestamp?: number;
  end_timestamp?: number;
  simulation_period_type?: string;
};

export type PeriodTimestamp = PpaPeriodTimestamp;

type ScenarioManagerScope = {
  dimension?: string;
  attribute_type?: string;
  attribute_ex_id?: string[];
};

type ScenarioManagerProject = {
  project_id: number;
  project_name: string;
  is_private?: boolean;
  created_by?: string;
  status?: string;
  last_updated?: string;
  simulation_period?: ScenarioManagerSimulationPeriod;
  scope?: ScenarioManagerScope[];
};

export type ScenarioManagerScenario = {
  scenario_id: number;
  scenario_name: string;
  created_by?: string;
  status?: string;
  last_updated?: string;
  simulation_period?: ScenarioManagerSimulationPeriod;
  scope?: ScenarioManagerScope[];
  ppa_scope_id?: number | null;
  analytics_enabled?: boolean;
};

type ScenariosState = {
  fetchedScenariosByProject: Record<number, ScenarioManagerScenario[]>;
  scenarioCountByProject: Record<number, number>;
  loadingScenarioProjectIds: Record<number, boolean>;
};

type ScenariosAction =
  | { type: 'SET_LOADING'; projectId: number; loading: boolean }
  | { type: 'SET_SCENARIOS'; projectId: number; scenarios: ScenarioManagerScenario[]; count: number }
  | { type: 'REMOVE_PROJECT'; projectId: number }
  | { type: 'REMOVE_SCENARIO'; projectId: number; scenarioId: number };

const initialScenariosState: ScenariosState = {
  fetchedScenariosByProject: {},
  scenarioCountByProject: {},
  loadingScenarioProjectIds: {},
};

const scenariosReducer = (state: ScenariosState, action: ScenariosAction): ScenariosState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loadingScenarioProjectIds: {
          ...state.loadingScenarioProjectIds,
          [action.projectId]: action.loading,
        },
      };
    case 'SET_SCENARIOS':
      return {
        ...state,
        fetchedScenariosByProject: {
          ...state.fetchedScenariosByProject,
          [action.projectId]: action.scenarios,
        },
        scenarioCountByProject: {
          ...state.scenarioCountByProject,
          [action.projectId]: action.count,
        },
      };
    case 'REMOVE_PROJECT': {
      const { [action.projectId]: _scenarios, ...fetchedScenariosByProject } =
        state.fetchedScenariosByProject;
      const { [action.projectId]: _count, ...scenarioCountByProject } = state.scenarioCountByProject;
      const { [action.projectId]: _loading, ...loadingScenarioProjectIds } =
        state.loadingScenarioProjectIds;
      return { fetchedScenariosByProject, scenarioCountByProject, loadingScenarioProjectIds };
    }
    case 'REMOVE_SCENARIO': {
      const existingScenarios = state.fetchedScenariosByProject[action.projectId];
      if (!existingScenarios) {
        return state;
      }
      const nextScenarios = existingScenarios.filter(
        (scenario) => String(scenario.scenario_id) !== String(action.scenarioId),
      );
      if (nextScenarios.length === existingScenarios.length) {
        return state;
      }
      const existingCount = state.scenarioCountByProject[action.projectId];
      return {
        ...state,
        fetchedScenariosByProject: {
          ...state.fetchedScenariosByProject,
          [action.projectId]: nextScenarios,
        },
        scenarioCountByProject:
          typeof existingCount === 'number'
            ? {
                ...state.scenarioCountByProject,
                [action.projectId]: Math.max(existingCount - 1, 0),
              }
            : state.scenarioCountByProject,
      };
    }
    default:
      return state;
  }
};

const loadScenariosForProject = async (projectId: number) => {
  const response = await axios.post(ppaUrls.getScenarioListUrl, { project_id: projectId, user_id: getAuthUserMail() || null });
  const scenarios = getPpaScenariosFromResponse<ScenarioManagerScenario>(response.data);
  const scenarioCount = getPpaScenarioCountFromResponse(response.data, scenarios.length);
  return { scenarios, scenarioCount };
};

type PriceSimulatorV3ContextValue = {
  scenarioManagerMeta: ScenarioManagerMetaResponse | undefined;
  isScenarioManagerMetaLoading: boolean;
  isScenarioManagerMetaPending: boolean;
  isProjectsLoading: boolean;
  isProjectsPending: boolean;
  isProjectsRefetching: boolean;
  isFetchingNextProjectsPage: boolean;
  hasNextProjectsPage: boolean;
  fetchNextProjectsPage: () => Promise<unknown>;
  myScenarioOnly: boolean;
  setMyScenarioOnly: React.Dispatch<React.SetStateAction<boolean>>;
  projectSearch: string;
  setProjectSearch: React.Dispatch<React.SetStateAction<string>>;
  projectSort?: ProjectListSort;
  setProjectSort: React.Dispatch<React.SetStateAction<ProjectListSort | undefined>>;
  projects: ScenarioManagerProject[];
  scenariosByProject: Record<number, ScenarioManagerScenario[]>;
  scenarioCountByProject: Record<number, number>;
  loadingScenarioProjectIds: Record<number, boolean>;
  fetchScenariosByProject: (projectId: number) => Promise<void>;
  refreshScenariosForProject: (projectId: number) => Promise<void>;
  removeProjectFromScenarioState: (projectId: number) => void;
  removeScenarioFromProjectState: (projectId: number, scenarioId: number) => void;
  periodTimestamps: PeriodTimestamp[];
  isPeriodTimestampsLoading: boolean;
};

const PriceSimulatorV3DataContext = createContext<PriceSimulatorV3ContextValue>({
  scenarioManagerMeta: undefined,
  isScenarioManagerMetaLoading: false,
  isScenarioManagerMetaPending: false,
  isProjectsLoading: false,
  isProjectsPending: false,
  isProjectsRefetching: false,
  isFetchingNextProjectsPage: false,
  hasNextProjectsPage: false,
  fetchNextProjectsPage: async () => undefined,
  myScenarioOnly: true,
  setMyScenarioOnly: () => undefined,
  projectSearch: '',
  setProjectSearch: () => undefined,
  projectSort: undefined,
  setProjectSort: () => undefined,
  projects: [],
  scenariosByProject: {},
  scenarioCountByProject: {},
  loadingScenarioProjectIds: {},
  fetchScenariosByProject: async () => undefined,
  refreshScenariosForProject: async () => undefined,
  removeProjectFromScenarioState: () => undefined,
  removeScenarioFromProjectState: () => undefined,
  periodTimestamps: [],
  isPeriodTimestampsLoading: false,
});

type Props = {
  children: React.ReactNode;
};

type ProjectListFilter = {
  id_type: 'column';
  id: string;
  filter: string[];
};

type ProjectListSort = {
  id_type: 'column';
  id: string;
  direction: 'asc' | 'desc';
};

type ProjectListRequestPayload = {
  my_scenario: boolean;
  search: string;
  offset: number;
  limit: number;
  filter?: ProjectListFilter[];
  sort?: ProjectListSort;
  user_id?: string;
};

/** Use with `queryClient.invalidateQueries` after create / edit / duplicate project. */
export const SCENARIO_MANAGER_PROJECT_LIST_QUERY_KEY = ['scenario-manager-project-list'] as const;

const getProjectsFromResponse = (responseData: any): ScenarioManagerProject[] => {
  if (Array.isArray(responseData?.projects)) {
    return responseData.projects;
  }
  if (Array.isArray(responseData?.project_list)) {
    return responseData.project_list;
  }
  return [];
};

const getProjectTotalFromResponse = (responseData: any): number | undefined => {
  const totalCandidates = [
    responseData?.total_count,
    responseData?.count,
    responseData?.total,
    responseData?.meta?.total_count,
    responseData?.meta?.total,
  ];

  for (const candidate of totalCandidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
};

type ProjectsPage = {
  projects: ScenarioManagerProject[];
  total?: number;
  offset: number;
  limit: number;
};

const PriceSimulatorV3Context: FC<Props> = ({ children }) => {
  const [myScenarioOnly, setMyScenarioOnly] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectSort, setProjectSort] = useState<ProjectListSort | undefined>(undefined);
  const [scenariosState, dispatchScenarios] = useReducer(scenariosReducer, initialScenariosState);
  const normalizedProjectSearch = projectSearch.trim();

  const {
    data: scenarioManagerMetaRaw,
    isLoading: isScenarioManagerMetaLoading,
    isPending: isScenarioManagerMetaPending,
  } = useQuery<ScenarioManagerMetaResponse>({
    queryKey: ['scenario-manager-meta'],
    queryFn: async () => {
      const response = await axios.get(ppaUrls.getScenarioManagerMetaUrl);
      return response.data;
    },
  });

  const presetPeriodIds = useMemo(
    () => getSimulationPeriodPresetIdsFromMeta(scenarioManagerMetaRaw),
    [scenarioManagerMetaRaw],
  );

  const {
    data: periodTimestampsData,
    isLoading: isPeriodTimestampsLoading,
  } = useQuery<PeriodTimestamp[]>({
    queryKey: ['ppa-period-start-end-timestamps', presetPeriodIds],
    enabled: Boolean(scenarioManagerMetaRaw) && presetPeriodIds.length > 0,
    queryFn: async () => {
      const response = await axios.post(ppaUrls.getPeriodStartEndTimestampsUrl, {
        period_id_list: presetPeriodIds,
      });
      return parsePeriodTimestampsResponse(response.data);
    },
  });

  const periodTimestamps = periodTimestampsData ?? [];

  const scenarioManagerMeta = useMemo(
    () => mergePeriodTimestampsIntoScenarioManagerMeta(scenarioManagerMetaRaw, periodTimestamps),
    [scenarioManagerMetaRaw, periodTimestamps],
  );

  const pageLimit = scenarioManagerMeta?.page_limit && scenarioManagerMeta.page_limit > 0
    ? scenarioManagerMeta.page_limit
    : 10;

  const {
    data: projectsInfiniteData,
    isLoading: isProjectsLoading,
    isPending: isProjectsPending,
    isFetching: isProjectsFetching,
    isFetchingNextPage: isFetchingNextProjectsPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<ProjectsPage>({
    queryKey: [
      ...SCENARIO_MANAGER_PROJECT_LIST_QUERY_KEY,
      pageLimit,
      myScenarioOnly,
      normalizedProjectSearch,
      projectSort?.id ?? null,
      projectSort?.direction ?? null,
    ],
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number;
      const appliedProjectFilters: ProjectListFilter[] = [];
      const appliedProjectSort: ProjectListSort | undefined = projectSort;

      const authMail = getAuthUserMail();
      const requestPayload: ProjectListRequestPayload = {
        my_scenario: myScenarioOnly,
        search: normalizedProjectSearch,
        offset,
        limit: pageLimit,
        ...(authMail ? { user_id: authMail } : {}),
      };

      if (appliedProjectFilters.length > 0) {
        requestPayload.filter = appliedProjectFilters;
      }

      if (appliedProjectSort) {
        requestPayload.sort = appliedProjectSort;
      }

      const response = await axios.post(ppaUrls.getProjectListUrl, requestPayload);
      const responseData = response.data;
      const pageProjects = getProjectsFromResponse(responseData);
      const total = getProjectTotalFromResponse(responseData);

      return {
        projects: pageProjects,
        total,
        offset,
        limit: pageLimit,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = lastPage.offset + lastPage.limit;
      const total = allPages[0]?.total;
      if (typeof total === 'number') {
        return nextOffset < total ? nextOffset : undefined;
      }
      return lastPage.projects.length < pageLimit ? undefined : nextOffset;
    },
    placeholderData: (previousData) => previousData,
  });

  const projects = projectsInfiniteData?.pages.flatMap((page) => page.projects) ?? [];
  const isProjectsRefetching = isProjectsFetching && !isFetchingNextProjectsPage;

  const fetchScenariosByProject = useCallback(
    async (projectId: number) => {
      if (
        scenariosState.fetchedScenariosByProject[projectId] !== undefined ||
        scenariosState.loadingScenarioProjectIds[projectId]
      ) {
        return;
      }

      dispatchScenarios({ type: 'SET_LOADING', projectId, loading: true });
      try {
        const { scenarios, scenarioCount } = await loadScenariosForProject(projectId);
        dispatchScenarios({ type: 'SET_SCENARIOS', projectId, scenarios, count: scenarioCount });
      } finally {
        dispatchScenarios({ type: 'SET_LOADING', projectId, loading: false });
      }
    },
    [scenariosState.fetchedScenariosByProject, scenariosState.loadingScenarioProjectIds],
  );

  const refreshScenariosForProject = useCallback(async (projectId: number) => {
    dispatchScenarios({ type: 'SET_LOADING', projectId, loading: true });
    try {
      const { scenarios, scenarioCount } = await loadScenariosForProject(projectId);
      dispatchScenarios({ type: 'SET_SCENARIOS', projectId, scenarios, count: scenarioCount });
    } finally {
      dispatchScenarios({ type: 'SET_LOADING', projectId, loading: false });
    }
  }, []);

  const removeProjectFromScenarioState = useCallback((projectId: number) => {
    dispatchScenarios({ type: 'REMOVE_PROJECT', projectId });
  }, []);

  const removeScenarioFromProjectState = useCallback((projectId: number, scenarioId: number) => {
    dispatchScenarios({ type: 'REMOVE_SCENARIO', projectId, scenarioId });
  }, []);

  return (
    <PriceSimulatorV3DataContext.Provider
      value={{
        scenarioManagerMeta,
        isScenarioManagerMetaLoading,
        isScenarioManagerMetaPending,
        isProjectsLoading,
        isProjectsPending,
        isProjectsRefetching,
        isFetchingNextProjectsPage,
        hasNextProjectsPage: Boolean(hasNextPage),
        fetchNextProjectsPage: fetchNextPage,
        myScenarioOnly,
        setMyScenarioOnly,
        projectSearch,
        setProjectSearch,
        projectSort,
        setProjectSort,
        projects,
        scenariosByProject: scenariosState.fetchedScenariosByProject,
        scenarioCountByProject: scenariosState.scenarioCountByProject,
        loadingScenarioProjectIds: scenariosState.loadingScenarioProjectIds,
        fetchScenariosByProject,
        refreshScenariosForProject,
        removeProjectFromScenarioState,
        removeScenarioFromProjectState,
        periodTimestamps,
        isPeriodTimestampsLoading,
      }}
    >
      {children}
    </PriceSimulatorV3DataContext.Provider>
  );
};

const usePriceSimulatorV3Context = () => useContext(PriceSimulatorV3DataContext);

export { PriceSimulatorV3Context, PriceSimulatorV3DataContext, usePriceSimulatorV3Context };
